const CommunityCommitmentSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Our <strong>Community Commitment</strong>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            We're more than just home buyers—we're invested in making El Paso a better place to live
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/c3f91b87d0781799f9a1850b3cd3cf70.jpg"
              alt="Community involvement El Paso"
              className="w-full h-full rounded-xl shadow-xl object-cover"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">
              Strengthening El Paso Neighborhoods
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Every property we purchase represents an opportunity to improve our community. We don't just buy homes and walk away—we invest in their renovation and restoration, returning quality housing to the market and helping to revitalize neighborhoods throughout <strong>El Paso, Texas</strong>.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Our renovation projects create jobs for local contractors, electricians, plumbers, and other tradespeople. We prioritize working with El Paso-based businesses, keeping our investment dollars circulating within the local economy. This commitment to local hiring has helped us build strong relationships throughout the construction and real estate communities.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We're particularly proud of our work in neighborhoods that have faced challenges. By purchasing and renovating distressed properties, we help eliminate blight, increase surrounding property values, and contribute to neighborhood stability. Our goal is to be a positive force in every community where we operate.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl shadow-lg p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold mb-8 text-gray-900 text-center">
            How We Give Back
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-home-smile-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Affordable Housing</h4>
                  <p className="text-gray-700">
                    Many of our renovated properties are sold to first-time homebuyers, helping El Paso families achieve homeownership.
                  </p>
                </div>
              </div>

              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Local Employment</h4>
                  <p className="text-gray-700">
                    Our projects provide steady work for dozens of local contractors and construction professionals.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-seedling-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Neighborhood Revitalization</h4>
                  <p className="text-gray-700">
                    We focus on improving properties in areas that need investment, helping to revitalize entire blocks.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-hand-heart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Community Support</h4>
                  <p className="text-gray-700">
                    We partner with local charities and organizations to support El Paso families in need.
                  </p>
                </div>
              </div>

              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-recycle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Sustainable Practices</h4>
                  <p className="text-gray-700">
                    Our renovations incorporate energy-efficient upgrades and sustainable materials whenever possible.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-gray-900">Code Compliance</h4>
                  <p className="text-gray-700">
                    We ensure all properties meet or exceed city building codes, improving neighborhood safety.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=before%20and%20after%20home%20renovation%20in%20El%20Paso%20Texas%2C%20split%20image%20showing%20transformation%2C%20distressed%20property%20to%20beautiful%20home%2C%20southwestern%20style%20house%2C%20dramatic%20improvement%2C%20professional%20real%20estate%20photography&width=800&height=500&seq=elpasocommunity002&orientation=landscape"
              alt="Home renovation before and after"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h4 className="text-2xl font-bold mb-3 text-gray-900">Transforming Properties</h4>
              <p className="text-gray-700 leading-relaxed">
                We take pride in transforming neglected properties into beautiful homes that families are proud to own. Each renovation project is an opportunity to preserve El Paso's architectural character while updating homes for modern living.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=happy%20diverse%20El%20Paso%20families%20in%20front%20of%20their%20homes%2C%20multi-generational%20community%2C%20southwestern%20neighborhood%2C%20children%20playing%2C%20friendly%20neighbors%2C%20warm%20community%20atmosphere%2C%20golden%20hour%20lighting&width=800&height=500&seq=elpasocommunity003&orientation=landscape"
              alt="El Paso community families"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h4 className="text-2xl font-bold mb-3 text-gray-900">Building Community</h4>
              <p className="text-gray-700 leading-relaxed">
                Our work helps create stable, thriving neighborhoods where families want to live. By improving housing stock and maintaining properties to high standards, we contribute to the overall quality of life in El Paso.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityCommitmentSection;
